import React, { useState, useEffect } from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const TEAL = "#4fd1c5";
const BG = "#0f1117";
const PANEL = "#161b24";
const BORDER = "#2d3748";
const TEXT = "#e8e6e1";
const MUTED = "#708898";
const ONBOARDING_KEY = "oe_onboarding_v1_shown";

interface OnboardingModalProps {
  onClose: () => void;
  onOpenSandbox?: () => void;
}

const STEPS = [
  {
    icon: <Icon.Ruler size={20}/>,
    title: "Build your estimate",
    body: "OpenEstimate is organized around CSI MasterFormat divisions — the industry-standard cost structure. Enable the divisions you need, add line items, and assign costs. Import an existing estimate via CSV or AI-powered PDF scan.",
  },
  {
    icon: <Icon.Users size={20}/>,
    title: "Collaborate with your team",
    body: "Share projects with teammates in real time. Assign roles (Editor, Viewer, Admin), organize into organizations, and track every change through the snapshot history. Multiple people can work in the same estimate simultaneously.",
  },
  {
    icon: <Icon.Upload size={20}/>,
    title: "Export anywhere",
    body: "Export to Excel, PDF, CSV, QuickBooks, or push directly to Procore's Budget module. Generate a full Scope of Work document in Word. Every format is ready from the File menu.",
  },
];

export default function OnboardingModal({ onClose, onOpenSandbox }: OnboardingModalProps) {
  const [step, setStep] = useState(0);

  const handleTrySandbox = () => {
    onClose();
    onOpenSandbox?.();
  };

  const isLast = step === STEPS.length - 1;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: FONT,
    }}>
      <div style={{
        background: PANEL, border: `1px solid ${BORDER}`,
        borderRadius: 10, width: 520, maxWidth: "100%",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        overflow: "hidden",
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: BORDER }}>
          <div style={{
            height: "100%", background: TEAL,
            width: `${((step + 1) / STEPS.length) * 100}%`,
            transition: "width 0.3s ease",
          }} />
        </div>

        <div style={{ padding: "36px 40px 32px" }}>
          {/* Welcome header — only on step 0 */}
          {step === 0 && (
            <div style={{ marginBottom: 28, textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
                <span style={{ color: TEXT }}>Welcome to Open</span>
                <span style={{ color: TEAL }}>Estimate</span>
              </div>
              <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>
                Here's a quick overview to get you started.
              </p>
            </div>
          )}

          {/* Step content */}
          <div style={{ textAlign: "center", padding: "8px 0 24px" }}>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>{STEPS[step].icon}</div>
            <h2 style={{
              fontSize: 20, fontWeight: 700, color: TEXT,
              margin: "0 0 12px", letterSpacing: "-0.01em",
            }}>
              {STEPS[step].title}
            </h2>
            <p style={{
              fontSize: 14, color: MUTED, lineHeight: 1.7,
              margin: "0 auto", maxWidth: 380,
            }}>
              {STEPS[step].body}
            </p>
          </div>

          {/* Step dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 28 }}>
            {STEPS.map((_, i) => (
              <div key={i} onClick={() => setStep(i)} style={{
                width: i === step ? 20 : 8, height: 8,
                borderRadius: 6, cursor: "pointer",
                background: i === step ? TEAL : BORDER,
                transition: "all 0.2s",
              }} />
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
            <button
              onClick={onClose}
              style={{
                background: "none", border: `1px solid ${BORDER}`,
                color: MUTED, borderRadius: 6, padding: "9px 18px",
                fontSize: 13, cursor: "pointer", fontFamily: FONT,
              }}
            >
              Skip
            </button>

            <div style={{ display: "flex", gap: 10 }}>
              {isLast && onOpenSandbox && (
                <button
                  onClick={handleTrySandbox}
                  style={{
                    background: "none", border: `1px solid ${TEAL}`,
                    color: TEAL, borderRadius: 6, padding: "9px 18px",
                    fontSize: 13, cursor: "pointer", fontFamily: FONT, fontWeight: 600,
                  }}
                >
                  Try sandbox demo
                </button>
              )}
              <button
                onClick={() => isLast ? onClose() : setStep(s => s + 1)}
                style={{
                  background: TEAL, border: "none", color: BG,
                  borderRadius: 6, padding: "9px 22px",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT,
                }}
              >
                {isLast ? <><span>Get started</span><Icon.ArrowRight size={14}/></> : <><span>Next</span><Icon.ArrowRight size={14}/></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Returns true if onboarding should be shown (first time user) */
export function shouldShowOnboarding(): boolean {
  try { return !sGetRaw(ONBOARDING_KEY); } catch { return false; }
}

/** Mark onboarding as seen */
export function markOnboardingSeen(): void {
  try { sSetRaw(ONBOARDING_KEY, "1"); } catch {}
}
